import MonacoEditor from '@monaco-editor/react';
import clsx from 'clsx';
import React, {
  memo, useCallback, useEffect, useRef
} from 'react';
import {
  FiBarChart2, FiEyeOff, FiPieChart, FiPlay, FiTrash2
} from 'react-icons/fi';

import { Button } from '@/components/button/button';
import { Icon } from '@/components/icon/icon';
import { Heading } from '@/components/typography/heading';

import styles from '../explorer.module.scss';


type Props = {
  value: string;
  onChange: ( value: string ) => void;
  onExecute: () => void;
  isExecuting: boolean;
  showChart?: boolean;
  onToggleChart?: () => void;
  showStats?: boolean;
  onToggleStats?: () => void;
  // KANP-1474: AuthZ gates for the active contract (derived from the SQL).
  // `canQuery` (default true) disables the Run button when the caller
  // lacks `can_query` on the contract. `canQueryPii` (default true) drives
  // the "PII columns hidden" badge — shown when the caller can query but
  // not see raw PII.
  canQuery?: boolean;
  canQueryPii?: boolean;
};

function SQLEditor( {
  value, onChange, onExecute, isExecuting,
  showChart, onToggleChart, showStats, onToggleStats,
  canQuery = true, canQueryPii = true
}: Props ) {
  const editorRef = useRef<unknown>( null );
  const onExecuteRef = useRef( onExecute );

  useEffect( () => {
    onExecuteRef.current = onExecute;
  }, [ onExecute ] );

  const handleEditorDidMount = useCallback( ( editor: unknown ) => {
    editorRef.current = editor;

    // Add Ctrl+Enter keybinding
    const monacoEditor = editor as { addCommand: ( keybinding: number, handler: () => void ) => void };
    // Monaco keybinding constants: Ctrl = 2048, Enter = 3
    monacoEditor.addCommand( 2048 | 3, () => {
      onExecuteRef.current();
    } );
  }, [] );

  const handleClear = useCallback( () => {
    onChange( '' );
  }, [ onChange ] );

  return (
    <div className={ styles.sqlEditor } data-testid="sql-editor">
      <div className={ styles.sqlToolbar }>
        <Heading as="h3" size="sm">SQL Editor</Heading>
        <div className={ styles.sqlToolbarActions }>
          { onToggleChart && (
            <button
              type="button"
              data-testid="toggle-chart-btn"
              className={ `${ styles.sqlToggleBtn } ${ showChart ? styles.sqlToggleBtn_active : '' }` }
              onClick={ onToggleChart }
              title={ showChart ? 'Hide chart panel' : 'Show chart panel' }
            >
              <FiBarChart2 size={ 14 } />
              Chart
            </button>
          ) }
          { onToggleStats && (
            <button
              type="button"
              data-testid="toggle-stats-btn"
              className={ `${ styles.sqlToggleBtn } ${ showStats ? styles.sqlToggleBtn_active : '' }` }
              onClick={ onToggleStats }
              title={ showStats ? 'Hide statistics panel' : 'Show statistics panel' }
            >
              <FiPieChart size={ 14 } />
              Stats
            </button>
          ) }
          { /* KANP-1474: PII-hidden indicator. Shown only when the
               caller IS authorized to query but NOT to see raw PII —
               signals the DE Agent will mask PII columns post-query.
               Hidden when canQuery=false (the Run button is gated;
               the badge would be noise) and when canQueryPii=true
               (nothing being hidden). */ }
          { canQuery && !canQueryPii && (
            <span
              className={ clsx( styles.sqlToggleBtn, styles.piiHiddenBadge ) }
              data-testid="pii-hidden-badge"
              title="You can query this contract, but PII columns will be masked in results."
            >
              <FiEyeOff size={ 14 } />
              PII hidden
            </span>
          ) }
          <Button
            variant="primary"
            size="sm"
            onClick={ onExecute }
            disabled={ isExecuting || !value.trim() || !canQuery }
            icon={ <Icon icon={ FiPlay } size="sm" /> }
            data-testid="sql-run-btn"
            title={ !canQuery ? 'You don\'t have query access on this contract.' : undefined }
          >
            Run
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={ handleClear }
            disabled={ !value }
            icon={ <Icon icon={ FiTrash2 } size="sm" /> }
            data-testid="sql-clear-btn"
          >
            Clear
          </Button>
        </div>
      </div>
      <div className={ styles.sqlEditorArea } data-testid="sql-editor-input">
        <MonacoEditor
          height="100%"
          language="sql"
          value={ value }
          onChange={ ( v: string | undefined ) => onChange( v || '' ) }
          onMount={ handleEditorDidMount }
          theme="vs"
          options={ {
            minimap: { enabled: false },
            fontSize: 13,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            automaticLayout: true,
            tabSize: 2,
            padding: { top: 8 },
            renderLineHighlight: 'line',
            suggestOnTriggerCharacters: true
          } }
        />
      </div>
    </div>
  );
}

export default memo( SQLEditor );
